import HttpClient from "./interfaces/HttpClient";
import CharacterData from "../model/CharacterData";
import CharacterOption from "../model/CharacterOption";
import CommissionType from "../model/CommissionType";
import WorldContent from "../model/WorldContent";
import {LoggingService} from "./LoggingService";
import TfGenResult from "../model/TfGenResult";
import TfGenQuery from "../model/TfGenQuery";
import GeneratorItemList from "../model/generator/GeneratorItemList";
import GuildCharacterData from "../model/GuildCharacterData";
import GymBadgeData from "../components/gym-badge/GymBadgeData";
import DonationInfo from "../model/DonationInfo";
import ComicMetadata from "../model/ComicMetadata";
import FursonaGenQuery from "../model/FursonaGenQuery";
import FursonaGenResult from "../model/FursonaGenResult";
import ShopEntry from "../model/ShopEntry";
import GalleryItem from "../model/GalleryItem";
import ChooseYourStarterSettings from "../model/pokemon/ChooseYourStarterSettings";
import ChooseYourStarterState from "../model/pokemon/ChooseYourStarterState";
import Environment from "./interfaces/Environment";
import _logging_service from "./LoggingService";
import _http_client from "../clients/AxiosHttpClient";
import _environment from "../environments/SiteEnvironment";

export class HttpService {

    constructor(private httpClient: HttpClient,
                private loggingService: LoggingService,
                private env: Environment) {}

    public async getComicChapterPages(comic: string, chapter: number): Promise<{[page: number] : ComicMetadata}> {
        try {
            return await this.httpClient.get<{[page: number] : ComicMetadata}>(
                `${this.env.targetServer}v1/comics/${comic}/chapters/${chapter}${this.env.fileExtension || ''}`);
        } catch (e) {
            this.loggingService.log(e);
            throw e;
        }
    }

    public async getComicAdvancePage(comic: string, chapter: number, page: number, id: string): Promise<ComicMetadata> {
        try {
            return await this.httpClient.get<ComicMetadata>(
                `${this.env.targetServer}v1/comics/${comic}/chapters/${chapter}/pages/${page}/advance/${id}${this.env.fileExtension || ''}`);
        } catch (e) {
            this.loggingService.log(e);
            throw e;
        }
    }

    public async getLatestComicPage(comic: string): Promise<number[]> {
        try {
            return await this.httpClient.get<number[]>(
                `${this.env.targetServer}v1/comics/${comic}/latest${this.env.fileExtension || ''}`);
        } catch (e) {
            this.loggingService.log(e);
            throw e;
        }
    }

    public async getCharacterOptions(): Promise<CharacterOption[]> {
        try {
            return await this.httpClient.get<CharacterOption[]>(
                `${this.env.targetServer}v1/characters${this.env.fileExtension || ''}`);
        } catch (e) {
            this.loggingService.log(e);
            throw e;
        }
    }

    public async getCharacterData(shortName: string): Promise<CharacterData> {
        try {
            return await this.httpClient.get<CharacterData>(
                `${this.env.targetServer}v1/characters/${shortName}${this.env.fileExtension || ''}`);
        } catch (e) {
            this.loggingService.log(e);
            throw e;
        }
    }

    public async getCommissionInfo(): Promise<{[p: string]: string}> {
        try {
            return await this.httpClient.get<{[name: string] : string}>(
                `${this.env.targetServer}v1/commissions/data${this.env.fileExtension || ''}`);
        } catch (e) {
            this.loggingService.log(e);
            throw e;
        }
    }

    public async getAllGalleryItems(gallery: string): Promise<GalleryItem[]> {
        try {
            return await this.httpClient.get<GalleryItem[]>(
                `${this.env.targetServer}v1/galleries/${gallery}${this.env.fileExtension || ''}`);
        } catch (e) {
            this.loggingService.log(e);
            throw e;
        }
    }

    public async getGalleryItem(gallery: string, id: string): Promise<GalleryItem> {
        try {
            return await this.httpClient.get<GalleryItem>(
                `${this.env.targetServer}v1/galleries/${gallery}/items/${id}${this.env.fileExtension || ''}`);
        } catch (e) {
            this.loggingService.log(e);
            throw e;
        }
    }

    public async getGuildCharacterOptions() {
        try {
            return await this.httpClient.get<CharacterOption[]>(
                `${this.env.targetServer}v1/guildcharacters${this.env.fileExtension || ''}`);
        } catch (e) {
            this.loggingService.log(e);
            throw e;
        }
    }

    public async getGuildCharacterData(shortName: string) {
        try {
            return await this.httpClient.get<GuildCharacterData>(
                `${this.env.targetServer}v1/guildcharacters/${shortName}${this.env.fileExtension || ''}`);
        } catch (e) {
            this.loggingService.log(e);
            throw e;
        }
    }

    public async getCommissionTypes(): Promise<CommissionType[]> {
        try {
            return await this.httpClient.get<CommissionType[]>(
                `${this.env.targetServer}v1/commissions/types${this.env.fileExtension || ''}`);
        } catch (e) {
            this.loggingService.log(e);
            throw e;
        }
    }

    public async getWorldContentByPath(path: string): Promise<WorldContent> {
        try {
            return await this.httpClient.get<WorldContent>(
                `${this.env.targetServer}v1/world/content/world/${path.replace(/^world\//g, '')}${this.env.fileExtension || ''}`);
        } catch (e) {
            this.loggingService.log(e);
            throw e;
        }
    }

    public async getWorldTopics(topic: string): Promise<string[]> {
        try {
            return await this.httpClient.get<string[]>(
                `${this.env.targetServer}v1/world/topics/${topic}${this.env.fileExtension || ''}`);
        } catch (e) {
            this.loggingService.log(e);
            throw e;
        }
    }

    public async getDonationsTotal(): Promise<number> {
        try {
            return await this.httpClient.get<number>(
                `${this.env.targetServer}v1/donations${this.env.fileExtension || ''}`);
        } catch (e) {
            this.loggingService.log(e);
            throw e;
        }
    }

    public async getDonations(key: string, year: string): Promise<DonationInfo> {
        try {
            return await this.httpClient.get<DonationInfo>(
                `${this.env.targetServer}v1/donations/${key}-${year}/twitch${this.env.fileExtension || ''}`);
        } catch (e) {
            this.loggingService.log(e);
            throw e;
        }
    }

    public async getTransformationGeneratorItems(): Promise<GeneratorItemList> {
        try {
            let url = `${this.env.targetServer}v1/generators/transformation/lists/species${this.env.fileExtension || ''}`;
            return await this.httpClient.get<GeneratorItemList>(url);
        } catch (e) {
            this.loggingService.log(e);
            throw e;
        }
    }

    public async getFursonaGeneratorItems(): Promise<GeneratorItemList> {
        try {
            let url = `${this.env.targetServer}v1/generators/fursona/lists/species${this.env.fileExtension || ''}`;
            return await this.httpClient.get<GeneratorItemList>(url);
        } catch (e) {
            this.loggingService.log(e);
            throw e;
        }
    }

    // TODO: Rework flags
    public async getTransformationGeneratorResult(query: TfGenQuery): Promise<TfGenResult> {
        try {
            let postQuery = {
                category: (query.category && query.category.toLowerCase() !== 'all') ? query.category.toUpperCase() : undefined,
                extinct: (query.extinct) ? query.extinct.toUpperCase() : 'EXCLUDE',
                mythical: (query.mythical) ? query.mythical.toUpperCase() : 'EXCLUDE',
                casarokanUnique: (query.casarokanUnique) ? query.casarokanUnique.toUpperCase() : 'EXCLUDE',
                modifierOption: query.modifier || 'NONE'
            };
            let url = `${this.env.targetServer}v1/generators/transformation${this.env.fileExtension || ''}`;
            return await this.httpClient.post<TfGenResult>(url, postQuery);
        } catch (e) {
            this.loggingService.log(e);
            throw e;
        }
    }

    public async getTransformationQuickResult(type?: string): Promise<TfGenResult> {
        if (!type) {
            type = 'quick';
        }

        try {
            let url = `${this.env.targetServer}v1/generators/transformation/${type}${this.env.fileExtension || ''}`;
            return await this.httpClient.get<TfGenResult>(url);
        } catch (e) {
            this.loggingService.log(e);
            throw e;
        }
    }

    public async getFursonaGeneratorResult(query: FursonaGenQuery): Promise<FursonaGenResult> {
        try {
            let postQuery = {
                category: (query.category && query.category.toLowerCase() !== 'all') ? query.category.toUpperCase() : undefined,
                colors: query.colors || 0,
                colorSet: query.colorSet || 'ALL',
                modifierOption: query.modifier || 'NONE'
            };
            let url = `${this.env.targetServer}v1/generators/fursona${this.env.fileExtension || ''}`;
            return await this.httpClient.post<FursonaGenResult>(url, postQuery);
        } catch (e) {
            this.loggingService.log(e);
            throw e;
        }
    }

    public async startChooseYourStarterGame(settings: ChooseYourStarterSettings): Promise<string> {
        try {
            let url = `${this.env.targetServer}v1/tools/pokemon/choose-your-starter${this.env.fileExtension || ''}`;
            return await this.httpClient.post<string>(url, settings);
        } catch (e) {
            this.loggingService.log(e);
            throw e;
        }
    }

    public async getChooseYourStarterGameState(gameId: string): Promise<ChooseYourStarterState> {
        try {
            let url = `${this.env.targetServer}v1/tools/pokemon/choose-your-starter/${gameId}${this.env.fileExtension || ''}`;
            return await this.httpClient.get<ChooseYourStarterState>(url);
        } catch (e) {
            this.loggingService.log(e);
            throw e;
        }
    }

    public async selectChooseYourStarterGamePokemon(gameId: string, pokemonId: string): Promise<ChooseYourStarterState> {
        try {
            let url = `${this.env.targetServer}v1/tools/pokemon/choose-your-starter/${gameId}${this.env.fileExtension || ''}`;
            return await this.httpClient.post<ChooseYourStarterState>(url, pokemonId);
        } catch (e) {
            this.loggingService.log(e);
            throw e;
        }
    }

    public async endChooseYourStarterGame(gameId: string): Promise<void> {
        try {
            let url = `${this.env.targetServer}v1/tools/pokemon/choose-your-starter/${gameId}${this.env.fileExtension || ''}`;
            return await this.httpClient.delete<void>(url);
        } catch (e) {
            this.loggingService.log(e);
            throw e;
        }
    }

    public async getPokemonPlayerNames(): Promise<string[]> {
        try {
            return await this.httpClient.get<string[]>(
                `${this.env.targetServer}v1/stream/games/pokemon/players${this.env.fileExtension || ''}`);
        } catch (e) {
            this.loggingService.log(e);
            throw e;
        }
    }

    public async getPokemonPlayerBadges(username: string): Promise<GymBadgeData[]> {
        try {
            return await this.httpClient.get<GymBadgeData[]>(
                `${this.env.targetServer}v1/stream/games/pokemon/players/${username}${this.env.fileExtension || ''}`);
        } catch (e) {
            this.loggingService.log(e);
            throw e;
        }
    }

    public async getShopEntries(): Promise<ShopEntry[]> {
        try {
            return await this.httpClient.get<ShopEntry[]>(
                `${this.env.targetServer}v1/shop/entries${this.env.fileExtension || ''}`);
        } catch (e) {
            this.loggingService.log(e);
            throw e;
        }
    }

    public async getDownloadLocation(item: string): Promise<string> {
        try {
            return await this.httpClient.get<string>(
                `${this.env.targetServer}v1/downloads/${item}${this.env.fileExtension || ''}`);
        } catch (e) {
            this.loggingService.log(e);
            throw e;
        }
    }
}

const _http_service = new HttpService(_http_client, _logging_service, _environment);

export default _http_service;